import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import DashboardTemplate from '../../Common/DashboardTemplate';
import {
    Button, CircularProgress, TextField, Checkbox, FormControlLabel,
    Select, MenuItem, FormControl, InputLabel, Divider, IconButton
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { v4 as uuid } from 'uuid';
import { ServiceLocator } from '../../../Services/ServiceLocator';
import CustomerSiteProductTabsPanel from './CustomerSiteProductTabsPanel';

const columnTileStyle = {
    padding: '20px',
    backgroundColor: 'white',
    borderRadius: 8,
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    marginBottom: 0,
    marginTop: 0,
    width: '100%',
};

const fullWidthTileStyle = {
    padding: '20px',
    backgroundColor: 'white',
    borderRadius: 8,
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    marginBottom: 30,
    marginTop: 20,
    width: '100%',
    maxWidth: 1800,
    marginLeft: 'auto',
    marginRight: 'auto',
};

const styles = theme => ({
    optionBlock: {
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
        border: '1px solid #e0e0e0',
        borderRadius: 4,
    },
    choiceBlock: {
        marginLeft: theme.spacing(2),
        marginTop: theme.spacing(1),
        padding: theme.spacing(1),
        borderLeft: '3px solid #35d2d9',
    },
});

const emptyListing = () => ({
    id: '',
    listingKey: '',
    slug: '',
    title: '',
    customerSiteDescription: '',
    customerProductDescriptionHtml: '',
    customerProductTabsJson: '[]',
    isPublished: true,
    isDiscontinued: false,
    isActive: true,
    chassisFor: '',
    assemblyType: '',
    manufacturerFor: '',
    tertiaryType: '',
    brand: '',
    imageURL: '',
    imageURLs: [''],
    installVideoURL: '',
    labelDescription: '',
    sortOrder: 0,
    standardCatalogProductType: null,
    standardCatalogProductId: null,
    standardQuantity: 1,
    options: [],
    additionalItems: [],
    vehicleFitments: [],
});

class EditProductListing extends Component {
    state = {
        loading: true,
        saving: false,
        uploadingListingImage: false,
        listing: emptyListing(),
        listingSummaries: [],
        assemblies: [],
        subAssemblies: [],
        parts: [],
    };

    componentDidMount() {
        Promise.all([this.loadCatalogRefs(), this.loadListingSummaries()]).then(() => this.loadListing());
    }

    loadCatalogRefs = async () => {
        const assemblyService = ServiceLocator.Instance.getAssemblyService();
        const [assemblies, subAssemblies, parts] = await Promise.all([
            assemblyService.getAllAssembliesExpanded(),
            assemblyService.getAllSubAssembliesExpanded(),
            ServiceLocator.Instance.getPartService().getAllPartsExpanded(),
        ]);
        this.setState({ assemblies, subAssemblies, parts });
    };

    loadListingSummaries = async () => {
        try {
            const listingSummaries = await ServiceLocator.Instance.getProductListingService().getAll();
            this.setState({ listingSummaries: listingSummaries || [] });
        } catch {
            this.setState({ listingSummaries: [] });
        }
    };

    parseImageURLs = (imageURL) => {
        if (!imageURL || typeof imageURL !== 'string') return [''];
        const urls = imageURL.split('~|~').filter(x => x !== undefined);
        return urls.length > 0 ? urls : [''];
    };

    loadListing = () => {
        const { listingId } = this.props.match.params;
        if (!listingId || listingId === 'new') {
            this.setState({ loading: false, listing: emptyListing() });
            return;
        }

        ServiceLocator.Instance.getProductListingService()
            .getById(listingId)
            .then(listing => {
                const additionalItems = (listing.additionalItems || []).map(item => ({
                    ...item,
                    itemType: item.linkedProductListingId ? 'linked' : 'direct',
                }));
                const imageURLs = this.parseImageURLs(listing.imageURL);
                this.setState({
                    listing: {
                        ...listing,
                        additionalItems,
                        imageURLs,
                        vehicleFitments: this.normalizeFitmentGroups(listing),
                    },
                    loading: false,
                });
            })
            .catch(err => {
                window.$alertError(err.message || 'Failed to load listing');
                this.setState({ loading: false });
            });
    };

    updateField = (field, value) => {
        this.setState(prev => ({ listing: { ...prev.listing, [field]: value } }));
    };

    getSkuOptions = () => {
        const { assemblies, subAssemblies, parts } = this.state;
        const options = [];
        assemblies.filter(a => a.assembly.isActive).forEach(a => {
            options.push({
                key: `assembly-${a.assembly.id}`,
                catalogProductType: 'Assembly',
                catalogProductId: a.assembly.id,
                label: `${a.assembly.assemblyId} rev ${a.assembly.revision}`,
            });
        });
        subAssemblies.filter(s => s.subAssembly.isActive).forEach(s => {
            options.push({
                key: `subassembly-${s.subAssembly.id}`,
                catalogProductType: 'Subassembly',
                catalogProductId: s.subAssembly.id,
                label: `${s.subAssembly.subAssemblyId} rev ${s.subAssembly.revision}`,
            });
        });
        parts.filter(p => p.part.isActive).forEach(p => {
            options.push({
                key: `part-${p.part.id}`,
                catalogProductType: 'Part',
                catalogProductId: p.part.id,
                label: `${p.part.partId} rev ${p.part.revision}`,
            });
        });
        return options.sort((a, b) => a.label.localeCompare(b.label));
    };

    getStandardSkuValue = () => {
        const { listing } = this.state;
        if (!listing.standardCatalogProductId) return null;
        return this.getSkuOptions().find(
            o => o.catalogProductId === listing.standardCatalogProductId
                && o.catalogProductType === listing.standardCatalogProductType
        ) || null;
    };

    setStandardSku = (val) => {
        if (!val) {
            this.setState(prev => ({
                listing: {
                    ...prev.listing,
                    standardCatalogProductType: null,
                    standardCatalogProductId: null,
                },
            }));
            return;
        }
        this.setState(prev => ({
            listing: {
                ...prev.listing,
                standardCatalogProductType: val.catalogProductType,
                standardCatalogProductId: val.catalogProductId,
            },
        }));
    };

    getListingLinkOptions = () => {
        const { listingSummaries, listing } = this.state;
        return (listingSummaries || [])
            .filter(l => l.id && l.id !== listing.id)
            .map(l => ({
                id: l.id,
                label: `${l.listingKey || l.title} — ${l.title || ''}`.trim(),
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
    };

    addAdditionalItem = () => {
        const items = [...(this.state.listing.additionalItems || [])];
        items.push({
            id: uuid(),
            label: 'Additional product',
            sortOrder: items.length,
            isOptional: true,
            itemType: 'direct',
            catalogProductType: 'Assembly',
            catalogProductId: '',
            quantity: 1,
            linkedProductListingId: null,
        });
        this.updateField('additionalItems', items);
    };

    updateAdditionalItem = (itemId, patch) => {
        const items = (this.state.listing.additionalItems || []).map(item =>
            item.id === itemId ? { ...item, ...patch } : item
        );
        this.updateField('additionalItems', items);
    };

    removeAdditionalItem = (itemId) => {
        const items = (this.state.listing.additionalItems || []).filter(item => item.id !== itemId);
        this.updateField('additionalItems', items);
    };

    setAdditionalItemSku = (itemId, val) => {
        if (!val) {
            this.updateAdditionalItem(itemId, {
                catalogProductType: 'Assembly',
                catalogProductId: '',
            });
            return;
        }
        this.updateAdditionalItem(itemId, {
            catalogProductType: val.catalogProductType,
            catalogProductId: val.catalogProductId,
        });
    };

    setAdditionalItemListing = (itemId, listingOption) => {
        if (!listingOption) {
            this.updateAdditionalItem(itemId, { linkedProductListingId: null });
            return;
        }
        this.updateAdditionalItem(itemId, {
            linkedProductListingId: listingOption.id,
            catalogProductType: null,
            catalogProductId: null,
        });
    };

    setAdditionalItemType = (itemId, itemType) => {
        if (itemType === 'linked') {
            this.updateAdditionalItem(itemId, {
                itemType: 'linked',
                catalogProductType: null,
                catalogProductId: null,
            });
        } else {
            this.updateAdditionalItem(itemId, {
                itemType: 'direct',
                linkedProductListingId: null,
                catalogProductType: 'Assembly',
                catalogProductId: '',
            });
        }
    };

    addOption = (parentChoiceId = null) => {
        const option = {
            id: uuid(),
            parentChoiceId,
            label: 'New dropdown',
            sortOrder: this.state.listing.options.length,
            isRequired: true,
            choices: [],
        };
        this.updateField('options', [...this.state.listing.options, option]);
    };

    updateOption = (optionId, patch) => {
        const options = this.state.listing.options.map(o => o.id === optionId ? { ...o, ...patch } : o);
        this.updateField('options', options);
    };

    removeOption = (optionId) => {
        const options = this.state.listing.options.filter(o => o.id !== optionId && o.parentChoiceId !== optionId);
        this.updateField('options', options);
    };

    addChoice = (optionId) => {
        const options = this.state.listing.options.map(o => {
            if (o.id !== optionId) return o;
            const choice = { id: uuid(), label: 'New choice', sortOrder: o.choices.length, lineItems: [] };
            return { ...o, choices: [...o.choices, choice] };
        });
        this.updateField('options', options);
    };

    updateChoice = (optionId, choiceId, patch) => {
        const options = this.state.listing.options.map(o => {
            if (o.id !== optionId) return o;
            return {
                ...o,
                choices: o.choices.map(c => c.id === choiceId ? { ...c, ...patch } : c),
            };
        });
        this.updateField('options', options);
    };

    addLineItem = (optionId, choiceId) => {
        const skuOptions = this.getSkuOptions();
        const first = skuOptions[0];
        const options = this.state.listing.options.map(o => {
            if (o.id !== optionId) return o;
            return {
                ...o,
                choices: o.choices.map(c => {
                    if (c.id !== choiceId) return c;
                    const li = {
                        id: uuid(),
                        catalogProductType: first?.catalogProductType || 'Assembly',
                        catalogProductId: first?.catalogProductId || '',
                        quantity: 1,
                        sortOrder: c.lineItems.length,
                        displayLabel: '',
                    };
                    return { ...c, lineItems: [...c.lineItems, li] };
                }),
            };
        });
        this.updateField('options', options);
    };

    updateLineItem = (optionId, choiceId, lineItemId, patch) => {
        const options = this.state.listing.options.map(o => {
            if (o.id !== optionId) return o;
            return {
                ...o,
                choices: o.choices.map(c => {
                    if (c.id !== choiceId) return c;
                    return {
                        ...c,
                        lineItems: c.lineItems.map(li => li.id === lineItemId ? { ...li, ...patch } : li),
                    };
                }),
            };
        });
        this.updateField('options', options);
    };

    parseChassisList = (value) => {
        if (!value) return [];
        const seen = new Set();
        const out = [];
        String(value).split(/[;,\n\r]+/).forEach((raw) => {
            const code = String(raw || '').trim();
            if (!code) return;
            const key = code.toLowerCase();
            if (seen.has(key)) return;
            seen.add(key);
            out.push(code);
        });
        return out;
    };

    normalizeFitmentGroups = (listing) => {
        const fromApi = Array.isArray(listing.vehicleFitments) ? listing.vehicleFitments : [];
        if (fromApi.length > 0) {
            return fromApi.map((group) => ({
                key: uuid(),
                manufacturer: group.manufacturer || '',
                chassis: Array.isArray(group.chassis) ? group.chassis.filter(Boolean) : this.parseChassisList(group.chassis),
            }));
        }
        const manufacturer = (listing.manufacturerFor || '').trim();
        const chassis = this.parseChassisList(listing.chassisFor);
        if (!manufacturer && chassis.length === 0) return [];
        return [{ key: uuid(), manufacturer, chassis }];
    };

    addFitmentMake = () => {
        const vehicleFitments = [...(this.state.listing.vehicleFitments || []), {
            key: uuid(),
            manufacturer: '',
            chassis: [],
        }];
        this.updateField('vehicleFitments', vehicleFitments);
    };

    removeFitmentMake = (key) => {
        this.updateField(
            'vehicleFitments',
            (this.state.listing.vehicleFitments || []).filter((group) => group.key !== key)
        );
    };

    updateFitmentManufacturer = (key, manufacturer) => {
        this.updateField(
            'vehicleFitments',
            (this.state.listing.vehicleFitments || []).map((group) =>
                group.key === key ? { ...group, manufacturer } : group
            )
        );
    };

    updateFitmentChassis = (key, chassis) => {
        const seen = new Set();
        const next = [];
        (Array.isArray(chassis) ? chassis : []).forEach((raw) => {
            const code = String(raw || '').trim();
            if (!code) return;
            const id = code.toLowerCase();
            if (seen.has(id)) return;
            seen.add(id);
            next.push(code);
        });
        this.updateField(
            'vehicleFitments',
            (this.state.listing.vehicleFitments || []).map((group) =>
                group.key === key ? { ...group, chassis: next } : group
            )
        );
    };

    buildSavePayload = () => {
        const { listing } = this.state;
        const { listingId } = this.props.match.params;
        const isNew = !listingId || listingId === 'new';

        const payload = { ...listing };
        if (isNew || !payload.id) {
            delete payload.id;
        }

        if (Array.isArray(payload.options)) {
            payload.options = payload.options.map(opt => ({
                ...opt,
                choices: (opt.choices || []).map(choice => ({
                    ...choice,
                    lineItems: (choice.lineItems || []).filter(li => li.catalogProductId),
                })),
            }));
        }

        payload.additionalItems = (payload.additionalItems || [])
            .map((item, index) => {
                const cleaned = {
                    label: item.label || '',
                    sortOrder: typeof item.sortOrder === 'number' ? item.sortOrder : index,
                    isOptional: !!item.isOptional,
                    quantity: item.quantity > 0 ? item.quantity : 1,
                };
                if (item.linkedProductListingId) {
                    cleaned.linkedProductListingId = item.linkedProductListingId;
                } else if (item.catalogProductId) {
                    cleaned.catalogProductType = item.catalogProductType || 'Assembly';
                    cleaned.catalogProductId = item.catalogProductId;
                }
                return cleaned;
            })
            .filter(item => item.linkedProductListingId || item.catalogProductId);

        if (!payload.standardCatalogProductId) {
            payload.standardCatalogProductType = null;
            payload.standardCatalogProductId = null;
        }
        payload.standardQuantity = payload.standardQuantity > 0 ? payload.standardQuantity : 1;

        if (Array.isArray(payload.imageURLs)) {
            payload.imageURL = payload.imageURLs.filter(x => x && x.trim()).join('~|~');
        }
        delete payload.imageURLs;

        payload.vehicleFitments = (payload.vehicleFitments || [])
            .map((group) => ({
                manufacturer: (group.manufacturer || '').trim(),
                chassis: Array.isArray(group.chassis) ? group.chassis.map(c => String(c).trim()).filter(Boolean) : [],
            }))
            .filter((group) => group.manufacturer && group.chassis.length > 0);

        return payload;
    };

    addNewImage = () => {
        const imageURLs = [...(this.state.listing.imageURLs || ['']), ''];
        this.updateField('imageURLs', imageURLs);
    };

    changeImageURL = (index, event) => {
        const imageURLs = [...(this.state.listing.imageURLs || [''])];
        imageURLs[index] = event.target.value;
        this.updateField('imageURLs', imageURLs);
    };

    removeImage = (index) => {
        const imageURLs = [...(this.state.listing.imageURLs || [''])];
        imageURLs.splice(index, 1);
        if (imageURLs.length === 0) imageURLs.push('');
        this.updateField('imageURLs', imageURLs);
    };

    processListingImageFiles = async (files) => {
        if (!files || files.length === 0) return;

        const { listing } = this.state;
        if (!listing.id) {
            window.$alertError('Save the listing before uploading images.');
            return;
        }

        const invalidFiles = files.filter(file => !file.type.startsWith('image/'));
        if (invalidFiles.length > 0) {
            window.$alertError('Please select valid image files only.');
            return;
        }

        const oversizedFiles = files.filter(file => file.size > 10 * 1024 * 1024);
        if (oversizedFiles.length > 0) {
            window.$alertError('All files must be less than 10MB.');
            return;
        }

        this.setState({ uploadingListingImage: true });
        const service = ServiceLocator.Instance.getProductListingService();
        const uploadedUrls = [];
        let successCount = 0;
        let errorCount = 0;

        try {
            for (let i = 0; i < files.length; i++) {
                try {
                    const response = await service.uploadImage(listing.id, files[i]);
                    uploadedUrls.push(response.imageUrl);
                    successCount++;
                } catch (error) {
                    console.error(`Error uploading listing image ${i + 1}:`, error);
                    errorCount++;
                }
            }

            if (uploadedUrls.length > 0) {
                const imageURLs = [...(listing.imageURLs || [''])];
                let blankIndex = imageURLs.findIndex(url => !url || url.trim() === '');

                uploadedUrls.forEach((url) => {
                    if (blankIndex !== -1 && blankIndex < imageURLs.length) {
                        imageURLs[blankIndex] = url;
                        blankIndex = imageURLs.findIndex((item, idx) => idx > blankIndex && (!item || item.trim() === ''));
                    } else {
                        imageURLs.push(url);
                    }
                });

                this.updateField('imageURLs', imageURLs);

                if (successCount === files.length) {
                    window.$alertSuccess(`${successCount} image${successCount > 1 ? 's' : ''} uploaded successfully!`);
                } else {
                    window.$alertSuccess(`${successCount} image${successCount > 1 ? 's' : ''} uploaded successfully. ${errorCount} failed.`);
                }
            } else {
                window.$alertError('Failed to upload images. Please try again.');
            }
        } catch (error) {
            console.error('Error uploading listing images:', error);
            window.$alertError('Failed to upload images. Please try again.');
        } finally {
            this.setState({ uploadingListingImage: false });
        }
    };

    handleListingImageUpload = async (event) => {
        const files = Array.from(event.target.files || []);
        await this.processListingImageFiles(files);
        event.target.value = '';
    };

    formatSaveError = (err) => {
        const data = err?.response?.data;
        if (typeof data === 'string' && data.trim()) {
            return data.trim();
        }
        if (data && typeof data === 'object') {
            if (data.errors && typeof data.errors === 'object') {
                const parts = Object.entries(data.errors).flatMap(([field, msgs]) => {
                    const list = Array.isArray(msgs) ? msgs : [msgs];
                    return list
                        .filter(Boolean)
                        .map((msg) => (typeof msg === 'string' ? msg : `${field}: ${msg}`));
                });
                if (parts.length) return parts.join('\n');
            }
            if (typeof data.detail === 'string' && data.detail.trim()) return data.detail.trim();
            if (typeof data.message === 'string' && data.message.trim()) return data.message.trim();
            if (typeof data.title === 'string' && data.title.trim()
                && data.title !== 'One or more validation errors occurred.') {
                return data.title.trim();
            }
        }
        if (typeof err === 'string' && err.trim()) return err.trim();
        if (err?.message && !/^Request failed with status code \d+$/i.test(err.message)) {
            return err.message;
        }
        return 'Save failed';
    };

    save = () => {
        const { listingId } = this.props.match.params;
        const { listing } = this.state;
        const missing = [];
        if (!String(listing.listingKey || '').trim()) missing.push('Listing key');
        if (!String(listing.title || '').trim()) missing.push('Title');
        if (missing.length) {
            window.$alertError(`Missing required fields: ${missing.join(', ')}`);
            return;
        }

        const service = ServiceLocator.Instance.getProductListingService();
        const payload = this.buildSavePayload();
        this.setState({ saving: true });

        const promise = listingId && listingId !== 'new'
            ? service.update(listingId, payload)
            : service.create(payload);

        promise
            .then(saved => {
                window.$alertSuccess('Product listing saved');
                const additionalItems = (saved.additionalItems || []).map(item => ({
                    ...item,
                    itemType: item.linkedProductListingId ? 'linked' : 'direct',
                }));
                const imageURLs = this.parseImageURLs(saved.imageURL);
                const listing = { ...saved, additionalItems, imageURLs };
                if (listingId === 'new') {
                    this.props.history.replace(`/admin/product-listings/${saved.id}`);
                } else {
                    this.setState({ listing, saving: false });
                }
            })
            .catch(err => {
                window.$alertError(this.formatSaveError(err));
                this.setState({ saving: false });
            });
    };

    renderAdditionalItem = (item) => {
        const { classes } = this.props;
        const skuOptions = this.getSkuOptions();
        const listingLinkOptions = this.getListingLinkOptions();
        const itemType = item.itemType || (item.linkedProductListingId ? 'linked' : 'direct');
        const skuValue = skuOptions.find(
            o => o.catalogProductId === item.catalogProductId && o.catalogProductType === item.catalogProductType
        ) || null;
        const listingValue = listingLinkOptions.find(o => o.id === item.linkedProductListingId) || null;

        return (
            <div key={item.id} className={classes.optionBlock}>
                <TextField
                    fullWidth margin="dense" label="Label (shown on customer site)" variant="outlined"
                    value={item.label || ''}
                    onChange={e => this.updateAdditionalItem(item.id, { label: e.target.value })}
                />
                <FormControl fullWidth margin="dense" variant="outlined">
                    <InputLabel>Type</InputLabel>
                    <Select
                        value={itemType}
                        onChange={e => this.setAdditionalItemType(item.id, e.target.value)}
                        label="Type"
                    >
                        <MenuItem value="direct">Direct catalog SKU</MenuItem>
                        <MenuItem value="linked">Link to another product listing</MenuItem>
                    </Select>
                </FormControl>
                {itemType === 'direct' ? (
                    <Autocomplete
                        style={{ marginTop: 8 }}
                        options={skuOptions}
                        getOptionLabel={o => o.label}
                        value={skuValue}
                        onChange={(_, val) => this.setAdditionalItemSku(item.id, val)}
                        renderInput={params => <TextField {...params} label="SKU" margin="dense" variant="outlined" />}
                    />
                ) : (
                    <Autocomplete
                        style={{ marginTop: 8 }}
                        options={listingLinkOptions}
                        getOptionLabel={o => o.label}
                        value={listingValue}
                        onChange={(_, val) => this.setAdditionalItemListing(item.id, val)}
                        renderInput={params => <TextField {...params} label="Linked listing" margin="dense" variant="outlined" />}
                    />
                )}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', marginTop: 8 }}>
                    <TextField
                        type="number" label="Qty" margin="dense" variant="outlined" style={{ width: 100 }}
                        value={item.quantity || 1}
                        onChange={e => this.updateAdditionalItem(item.id, { quantity: parseInt(e.target.value, 10) || 1 })}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={!!item.isOptional}
                                onChange={e => this.updateAdditionalItem(item.id, { isOptional: e.target.checked })}
                                color="primary"
                            />
                        }
                        label="Optional (customer must opt in)"
                    />
                    <Button size="small" className="delete-button-lg" onClick={() => this.removeAdditionalItem(item.id)}>
                        Remove
                    </Button>
                </div>
            </div>
        );
    };

    renderOption = (option) => {
        const { classes } = this.props;
        const skuOptions = this.getSkuOptions();
        const parentChoices = this.state.listing.options.flatMap(o =>
            o.choices.map(c => ({ id: c.id, label: `${o.label} → ${c.label}` }))
        );

        return (
            <div key={option.id} className={classes.optionBlock}>
                <h4 style={{ marginTop: 0 }}>Dropdown: {option.label}</h4>
                <TextField
                    fullWidth margin="dense" label="Dropdown label" variant="outlined"
                    value={option.label}
                    onChange={e => this.updateOption(option.id, { label: e.target.value })}
                />
                <FormControl fullWidth margin="dense" variant="outlined">
                    <InputLabel>Show after choice (optional)</InputLabel>
                    <Select
                        value={option.parentChoiceId || ''}
                        onChange={e => this.updateOption(option.id, { parentChoiceId: e.target.value || null })}
                        label="Show after choice (optional)"
                    >
                        <MenuItem value=""><em>Root (always visible)</em></MenuItem>
                        {parentChoices.map(c => (
                            <MenuItem key={c.id} value={c.id}>{c.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControlLabel
                    control={<Checkbox checked={option.isRequired} onChange={e => this.updateOption(option.id, { isRequired: e.target.checked })} color="primary" />}
                    label="Required"
                />
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <Button size="small" className="success-button-lg" onClick={() => this.addChoice(option.id)}>Add choice</Button>
                    <Button size="small" className="delete-button-lg" onClick={() => this.removeOption(option.id)}>Remove dropdown</Button>
                </div>

                {option.choices.map(choice => (
                    <div key={choice.id} className={classes.choiceBlock}>
                        <TextField
                            fullWidth margin="dense" label="Choice label" variant="outlined"
                            value={choice.label}
                            onChange={e => this.updateChoice(option.id, choice.id, { label: e.target.value })}
                        />
                        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                            <Button size="small" className="success-button-lg" onClick={() => this.addLineItem(option.id, choice.id)}>Add SKU to cart</Button>
                            <Button size="small" className="success-button-lg" onClick={() => this.addOption(choice.id)}>Add child dropdown</Button>
                        </div>
                        {choice.lineItems.map(li => (
                            <div key={li.id} style={{ marginTop: 8, display: 'flex', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
                                <Autocomplete
                                    style={{ minWidth: 320, flex: 1 }}
                                    options={skuOptions}
                                    getOptionLabel={o => o.label}
                                    value={skuOptions.find(o => o.catalogProductId === li.catalogProductId && o.catalogProductType === li.catalogProductType) || null}
                                    onChange={(_, val) => {
                                        if (!val) return;
                                        this.updateLineItem(option.id, choice.id, li.id, {
                                            catalogProductType: val.catalogProductType,
                                            catalogProductId: val.catalogProductId,
                                        });
                                    }}
                                    renderInput={params => <TextField {...params} label="SKU" margin="dense" variant="outlined" />}
                                />
                                <TextField
                                    type="number" label="Qty" margin="dense" variant="outlined" style={{ width: 80 }}
                                    value={li.quantity}
                                    onChange={e => this.updateLineItem(option.id, choice.id, li.id, { quantity: parseInt(e.target.value, 10) || 1 })}
                                />
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        );
    };

    render() {
        const { listingId } = this.props.match.params;
        const { loading, saving, listing } = this.state;
        const rootOptions = listing.options.filter(o => !o.parentChoiceId);
        const pageTitle = listing.id ? `${listing.listingKey || listing.title}` : 'New Product Listing';

        if (loading) {
            return (
                <DashboardTemplate title="Product Listing">
                    <div className="main-dashboard-root">
                        <center style={{ padding: 40 }}><CircularProgress /></center>
                    </div>
                </DashboardTemplate>
            );
        }

        return (
            <DashboardTemplate title="Product Listing">
                <div className="main-dashboard-root">
                    <div style={{ marginBottom: 0, marginTop: -10 }}>
                        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', marginBottom: 10 }}>
                            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 15, justifyContent: 'flex-start' }}>
                                <Button
                                    onClick={() => this.props.history.push('/admin/product-listings')}
                                    className="success-button-lg"
                                >
                                    Back
                                </Button>
                                {saving ? (
                                    <CircularProgress size={28} />
                                ) : (
                                    <Button onClick={this.save} className="save-button-lg" style={{ backgroundColor: '#7fff84', color: 'black' }}>
                                        Save Changes
                                    </Button>
                                )}
                            </div>
                            <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>
                                <h1 style={{ margin: 0, textAlign: 'center', color: 'white', fontSize: '1.5rem' }}>{pageTitle}</h1>
                            </div>
                            <div style={{ flex: 1 }} />
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: '20px', maxWidth: 1800, margin: '15px auto 0', alignItems: 'flex-start' }}>
                        {/* Column 1: Listing details, Fitment, Images */}
                        <div style={{ flex: '1', minWidth: 0, maxWidth: '50%', display: 'flex', flexDirection: 'column', gap: '15px' }}>
                            <div className="container" style={columnTileStyle}>
                                <h2 style={{ marginTop: 0 }}>Listing details</h2>
                                <TextField fullWidth margin="dense" variant="outlined" label="Listing key (e.g. BMW01A0)" value={listing.listingKey} onChange={e => this.updateField('listingKey', e.target.value)} />
                                <TextField fullWidth margin="dense" variant="outlined" label="Title" value={listing.title} onChange={e => this.updateField('title', e.target.value)} />
                                <TextField fullWidth margin="dense" variant="outlined" label="Slug (URL)" value={listing.slug} onChange={e => this.updateField('slug', e.target.value)} />
                                <TextField fullWidth margin="dense" variant="outlined" label="Store description" value={listing.customerSiteDescription || ''} onChange={e => this.updateField('customerSiteDescription', e.target.value)} multiline />
                                <div style={{ marginTop: 8 }}>
                                    <FormControlLabel control={<Checkbox checked={listing.isPublished} onChange={e => this.updateField('isPublished', e.target.checked)} color="primary" />} label="Published on customer site" />
                                    <FormControlLabel control={<Checkbox checked={listing.isDiscontinued} onChange={e => this.updateField('isDiscontinued', e.target.checked)} color="primary" />} label="Discontinued" />
                                    <FormControlLabel control={<Checkbox checked={listing.isActive} onChange={e => this.updateField('isActive', e.target.checked)} color="primary" />} label="Active" />
                                </div>
                            </div>

                            <div className="container" style={columnTileStyle}>
                                <h2 style={{ marginTop: 0 }}>Fitment</h2>
                                <p style={{ color: '#666', marginTop: 0 }}>
                                    Add each manufacturer this listing fits, then type chassis / model codes as chips under that make.
                                    Leave fitment empty for universal parts — they appear for every shop-by-vehicle selection (below specific-fitment listings).
                                </p>
                                {(listing.vehicleFitments || []).map((group) => (
                                    <div key={group.key} style={{ border: '1px solid #e0e0e0', borderRadius: 4, padding: 12, marginBottom: 12 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                                            <TextField
                                                fullWidth
                                                margin="dense"
                                                variant="outlined"
                                                label="Make"
                                                placeholder="e.g. BMW"
                                                value={group.manufacturer}
                                                onChange={(e) => this.updateFitmentManufacturer(group.key, e.target.value)}
                                            />
                                            <IconButton aria-label="Remove make" onClick={() => this.removeFitmentMake(group.key)} size="small">
                                                <FontAwesomeIcon icon={faTrash} />
                                            </IconButton>
                                        </div>
                                        <Autocomplete
                                            multiple
                                            freeSolo
                                            options={[]}
                                            value={group.chassis || []}
                                            onChange={(event, values) => this.updateFitmentChassis(group.key, values)}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    variant="outlined"
                                                    label="Chassis / model"
                                                    placeholder="Type a code and press Enter"
                                                />
                                            )}
                                        />
                                    </div>
                                ))}
                                <Button variant="outlined" color="primary" onClick={this.addFitmentMake}>
                                    Add make
                                </Button>
                            </div>

                            <div className="container" style={columnTileStyle}>
                                <h2 style={{ marginTop: 0 }}>Images</h2>
                                <p style={{ color: '#666', marginTop: 0 }}>
                                    Shown on the customer product page. Dropdown SKU choices with their own images replace these when selected.
                                </p>
                                {!listing.id && (
                                    <p style={{ color: '#888', fontStyle: 'italic' }}>
                                        Save the listing first to upload image files. You can enter image URLs before saving.
                                    </p>
                                )}
                                {(listing.imageURLs || ['']).map((imageURL, index) => (
                                    <div key={index} style={{ marginBottom: 20 }}>
                                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: 10 }}>
                                            {(listing.imageURLs || []).length > 1 && (
                                                <div style={{ marginRight: 5 }} onClick={() => this.removeImage(index)}>
                                                    <FontAwesomeIcon
                                                        icon={faTrash}
                                                        size="1x"
                                                        style={{ width: 28, fontSize: 18, cursor: 'pointer' }}
                                                    />
                                                </div>
                                            )}
                                            <TextField
                                                value={imageURL}
                                                onChange={(event) => this.changeImageURL(index, event)}
                                                label="Image URL"
                                                variant="outlined"
                                                style={{ width: '80%' }}
                                                inputProps={{ maxLength: 500 }}
                                                autoComplete="off"
                                            />
                                            {imageURL && imageURL.trim() !== '' && (
                                                <img
                                                    src={imageURL}
                                                    width="18%"
                                                    alt={listing.title || listing.listingKey || 'Listing image'}
                                                    style={{ display: 'inline-block', marginLeft: 10 }}
                                                />
                                            )}
                                        </div>
                                    </div>
                                ))}
                                {listing.id && (
                                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: 10, marginBottom: 20 }}>
                                        <span style={{ marginRight: 10, fontSize: 14, color: '#666' }}>OR</span>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            multiple
                                            onChange={this.handleListingImageUpload}
                                            style={{ marginRight: 10, maxWidth: '180px', width: 'auto' }}
                                        />
                                        {this.state.uploadingListingImage && <CircularProgress size={20} />}
                                    </div>
                                )}
                                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}>
                                    <Button onClick={this.addNewImage} className="success-button-lg">Add Another Image</Button>
                                </div>
                            </div>
                        </div>

                        {/* Column 2: Standard SKU, SKU option tree, Additional products */}
                        <div style={{ flex: '1', minWidth: 0, maxWidth: '50%', display: 'flex', flexDirection: 'column', gap: '15px' }}>
                            <div className="container" style={columnTileStyle}>
                                <h2 style={{ marginTop: 0 }}>Standard SKU</h2>
                                <p style={{ color: '#666', marginTop: 0 }}>
                                    Default product added to cart when no dropdown choice overrides it. Selecting a dropdown choice with SKUs replaces this entirely.
                                </p>
                                <Autocomplete
                                    options={this.getSkuOptions()}
                                    getOptionLabel={o => o.label}
                                    value={this.getStandardSkuValue()}
                                    onChange={(_, val) => this.setStandardSku(val)}
                                    renderInput={params => <TextField {...params} label="Standard SKU" margin="dense" variant="outlined" />}
                                />
                                <TextField
                                    type="number"
                                    label="Quantity"
                                    margin="dense"
                                    variant="outlined"
                                    style={{ width: 120, marginTop: 8 }}
                                    value={listing.standardQuantity || 1}
                                    onChange={e => this.updateField('standardQuantity', parseInt(e.target.value, 10) || 1)}
                                />
                            </div>

                            <div className="container" style={columnTileStyle}>
                                <h2 style={{ marginTop: 0 }}>SKU option tree</h2>
                                <p style={{ color: '#666', marginTop: 0 }}>
                                    Configure dropdowns that map to assemblies, subassemblies, and parts. Child dropdowns appear when a parent choice is selected.
                                </p>
                                <Button variant="contained" className="success-button-lg action-button" onClick={() => this.addOption(null)}>
                                    Add root dropdown
                                </Button>
                                <Divider style={{ margin: '16px 0' }} />
                                {rootOptions.map(o => this.renderOption(o))}
                                {listing.options.filter(o => o.parentChoiceId).map(o => this.renderOption(o))}
                                {listing.options.length === 0 && (
                                    <p style={{ color: '#666', fontStyle: 'italic' }}>
                                        No dropdowns yet. Add a root dropdown to define SKU options for this listing.
                                    </p>
                                )}
                            </div>

                            <div className="container" style={columnTileStyle}>
                                <h2 style={{ marginTop: 0 }}>Additional products</h2>
                                <p style={{ color: '#666', marginTop: 0 }}>
                                    Spare hardware, linked sub-listings, or other add-ons shown on the customer product page.
                                </p>
                                <Button variant="contained" className="success-button-lg action-button" onClick={this.addAdditionalItem}>
                                    Add additional product
                                </Button>
                                <Divider style={{ margin: '16px 0' }} />
                                {(listing.additionalItems || []).map(item => this.renderAdditionalItem(item))}
                                {(listing.additionalItems || []).length === 0 && (
                                    <p style={{ color: '#666', fontStyle: 'italic' }}>No additional products configured.</p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Full-width: Customer site tabs & description */}
                    {listing.id ? (
                        <div className="container" style={fullWidthTileStyle}>
                            <h2 style={{ marginTop: 0 }}>Customer Site Displays</h2>
                            <CustomerSiteProductTabsPanel
                                embedded
                                productType="productlisting"
                                productId={listing.id}
                                displayName={listing.title || listing.listingKey}
                                showTemplateManager={false}
                            />
                        </div>
                    ) : listingId === 'new' ? (
                        <div className="container" style={{ ...fullWidthTileStyle, color: '#666' }}>
                            <h2 style={{ marginTop: 0 }}>Customer Site Displays</h2>
                            Save the listing first to unlock customer site tabs editing.
                        </div>
                    ) : null}
                </div>
            </DashboardTemplate>
        );
    }
}

EditProductListing.propTypes = {
    classes: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired,
};

export default withStyles(styles)(EditProductListing);
